/* eslint-disable max-lines */
import React, { useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'

import DownloadSystemReport from './DownloadSystemReport'
import EmailSystemReport from './EmailSystemReport'

import { ToastType } from 'components/Toast'
import Modal from 'components/Modal'

import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'

import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'

import { DeviceBase } from 'types/Device'
import { Customer } from 'types/Customer'

import Api from 'services/api'

import blobToFileDownload from 'helpers/blobToFileDownload'
import { useParams } from 'react-router-dom'
import { isCustomerNameUpdated, saveUpdatedCustomerName, validateForm, trackGoogleAnalyticsEvent } from 'helpers/systemReportsModalUtils'

type Props = {
  closeCallback: () => void
  isDisplayed: boolean
  devices: Array<DeviceBase>
  customer: Customer
  onSaveCallback: (deviceId: string) => void
  hasHehmDevices?: boolean
}

export type EmailToastType = {
  open: boolean
  title: string
  type: ToastType
  message: string
}

type SelectedDeviceIdAndFileName = {
  deviceId: string
  fileName: string
}

export type FormData = {
  selectedDeviceIdsAndFileNames: Array<SelectedDeviceIdAndFileName>,
  useServiceContractStatus: boolean,
  useSaveReportStatus: boolean,
  useImageOnFile: boolean,
  notes: string,
  consumerRthType: 'daily' | 'monthly',
  includeConsumerRthGraph: boolean,
  imageId: string,
  firstName: string,
  lastName: string
}

export const REPORT_TYPES = {
  'DOWNLOAD_SYSTEM_REPORT': 'System Report',
  'EMAIL_SYSTEM_REPORT': 'Email'
}

const SystemReportsModal = ({ closeCallback, isDisplayed, devices, customer, onSaveCallback, hasHehmDevices = false }: Props) => {
  const { deviceId } = useParams()

  const { session: { isImpersonating, dealerId } } = useSessionContext()

  const { setToast } = useStoreContext()

  const [formData, setFormData] = useState<FormData>({
    selectedDeviceIdsAndFileNames: [],
    useServiceContractStatus: true,
    useSaveReportStatus: !isImpersonating,
    useImageOnFile: true,
    notes: '',
    imageId: '',
    consumerRthType: 'monthly',
    includeConsumerRthGraph: true,
    firstName: customer?.firstName ?? '',
    lastName: customer?.lastName ?? ''
  })
  const [isDownloading, setIsDownloading] = useState(false)
  const [reportType, setReportType] = useState<string>(REPORT_TYPES.DOWNLOAD_SYSTEM_REPORT)
  const [errors, setErrors] = useState<{ devices?: string }>({})
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [isUpdateCustomerRecordChecked, setUpdateCustomerRecordChecked] = useState(true)
  const customerNameUpdated = isCustomerNameUpdated(formData.firstName, formData.lastName, customer)
  const displayCustomerName = isUpdateCustomerRecordChecked && customerNameUpdated
    ? `${formData.firstName} ${formData.lastName}`.trim()
    : `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`.trim()

  const handleFormData = (fieldName: string, fieldValue: string) => {
    setFormData((prev) => {
      const updated = { ...prev, [fieldName]: fieldValue }

      if (
        (fieldName === 'firstName' || fieldName === 'lastName') &&
        isCustomerNameUpdated(updated.firstName, updated.lastName, customer)
      ) {
        setUpdateCustomerRecordChecked(true)
      }

      return updated
    })

    if (fieldName === 'firstName' || fieldName === 'lastName') {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: !fieldValue.trim()
          ? `${fieldName === 'firstName' ? 'First name' : 'Last name'} is required` : ''
      }))
    }
  }

  const resetAndCloseModal = () => {
    setReportType(REPORT_TYPES.DOWNLOAD_SYSTEM_REPORT)
    setErrors({})
    closeCallback()
  }

  const generateReportPayload = (emailNotes: string, replyToEmail?: string, makeReplyToMailDefault?: boolean) => {
    const { useImageOnFile, imageId, notes, useServiceContractStatus, useSaveReportStatus, consumerRthType, includeConsumerRthGraph } = formData

    const selectedIds =
      devices.length === 1 ? [devices[0].deviceId] : formData.selectedDeviceIdsAndFileNames.map(item => item.deviceId)

    const timestamp = moment().format('MM-DD-YYYY_HH-mm-ss')
    const updatedSelectedDevices = selectedIds.map((deviceId) => {
      const fileName = `${customer.lastName}_${deviceId}_${timestamp}.pdf`
      return { deviceId, fileName }
    })

    setFormData((prev) => ({
      ...prev,
      selectedDeviceIdsAndFileNames: updatedSelectedDevices
    }))

    const payload = {
      customerId: customer?.id,
      useServiceContractStatus,
      useSaveReportStatus,
      consumerRthType,
      includeConsumerRthGraph,
      notes: reportType === REPORT_TYPES.EMAIL_SYSTEM_REPORT ? emailNotes : notes,
      reportGeneratedBy: displayCustomerName,
      replyToEmail: reportType === REPORT_TYPES.EMAIL_SYSTEM_REPORT ? replyToEmail : '',
      makeReplyToMailDefault: reportType === REPORT_TYPES.EMAIL_SYSTEM_REPORT ? makeReplyToMailDefault || false : false,
      useImageOnFile,
      imageId: useImageOnFile ? imageId : '',
      selectedDeviceIdsAndFileNames: JSON.stringify(updatedSelectedDevices)
    }

    return { payload, updatedSelectedDevices }
  }

  const handleUpdateCustomerNameRecord = () => {
    saveUpdatedCustomerName(dealerId, customer, formData.firstName, formData.lastName, isUpdateCustomerRecordChecked, onSaveCallback, setErrors, deviceId)
  }

  const selectEmailSystemReport = () => {
    const hasFormError = validateForm(formData, devices, setErrors)
    if (hasFormError) return
    setReportType(REPORT_TYPES.EMAIL_SYSTEM_REPORT)
  }

  const handleGenerateReport = () => {
    const hasFormError = validateForm(formData, devices, setErrors)
    if (hasFormError) return
    handleUpdateCustomerNameRecord()
    const { payload, updatedSelectedDevices } = generateReportPayload('')
    trackGoogleAnalyticsEvent('System Report PDF Generated', { ...formData, selectedDeviceIdsAndFileNames: updatedSelectedDevices })
    setIsDownloading(true)
    const filenameElements = updatedSelectedDevices[updatedSelectedDevices.length - 1].fileName.split('_')
    const timestamp = filenameElements[2]
    Api.SystemReports.getReport(payload).then((response) => {
      const reportFile = updatedSelectedDevices.length > 1 ? `${customer.lastName}_${timestamp}.zip` : updatedSelectedDevices[0].fileName
      blobToFileDownload(response, reportFile)
      closeCallback()
    }).catch(() => {
      setIsDownloading(false)
    })
  }

  const sendEmail = async (notes: string, replyToEmail: string, makeReplyToMailDefault: boolean) => {
    handleUpdateCustomerNameRecord()
    const { payload, updatedSelectedDevices } = generateReportPayload(notes, replyToEmail, makeReplyToMailDefault)
    trackGoogleAnalyticsEvent('System Report PDF Emailed', { ...formData, selectedDeviceIdsAndFileNames: updatedSelectedDevices })
    setIsEmailSent(true)

    Api.SystemReports.sendEmail(payload).then(() => {
      setToast({
        open: true,
        title: 'Success!',
        type: 'success',
        message: 'Your email has been sent.'
      })
    }).catch(() => {
      setToast({
        open: true,
        title: 'Error',
        type: 'error',
        message: 'Your email has not been sent.'
      })
    }).finally(() => {
      setIsEmailSent(false)
      resetAndCloseModal()
      setReportType(REPORT_TYPES.DOWNLOAD_SYSTEM_REPORT)
    })
  }

  return (
    <>
      <S.Modal
        title={reportType}
        variant="sm"
        closeCallback={resetAndCloseModal}
        showCloseButton={reportType === 'System Report'}
        {...{ isDisplayed }}
      >
        {reportType === REPORT_TYPES.DOWNLOAD_SYSTEM_REPORT ? (
          <DownloadSystemReport
            handleFormData={handleFormData}
            handleGenerateReport={handleGenerateReport}
            selectEmailSystemReport={selectEmailSystemReport}
            errors={errors}
            isDownloading={isDownloading}
            resetAndCloseModal={resetAndCloseModal}
            devices={devices}
            formData={formData}
            customerId={customer?.id}
            customerEmail={customer.email ?? ''}
            isUpdateCustomerRecordChecked={isUpdateCustomerRecordChecked}
            onUpdateCustomerRecordChange={setUpdateCustomerRecordChecked}
            customerNameUpdated={customerNameUpdated}
            hasHehmDevices={hasHehmDevices}
          />
        ) : (
          <EmailSystemReport
            sendEmail={sendEmail}
            resetAndCloseModal={resetAndCloseModal}
            customerName={displayCustomerName}
            customerEmail={customer.email ?? ''}
            isEmailSent={isEmailSent}
          />
        )}
      </S.Modal>
    </>
  )
}

export default SystemReportsModal

const S = {
  Modal: styled(Modal)`
    color: ${color('secondaryText')};
    @media ${screenSize.small} {
      padding-bottom: 70px;
    }
  `
}
